import PropTypes from 'prop-types';

import {
  Box,
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { useTransl } from 'shared/lib/i18n';

import ExpandableImageComponent from '../Image/ExpandableImageComponent';

import { avatarPlaceholderSRC, isAdmin } from '../../utils';

import ClockIcon from '../../res/svg/ClockIcon';
import LocationIcon from '../../res/svg/LocationIcon';
import LetterIcon from '../../res/svg/letter.svg';
import PenIcon from '../../res/svg/pen.svg';
import PhoneIcon from '../../res/svg/phone.svg';
import WithLink from '../Linkify';
import StyledLink from '../StyledLink';

const StyledParagraph = styled(Typography)(() => ({
  padding: 0,
  margin: 0,
  wordBreak: 'break-word',
}));

const StyledPhotoContainer = styled(Box)(({ theme }) => ({
  marginTop: 30,
  display: 'flex',
  gap: '2rem',
  [theme.breakpoints.up('xs')]: {
    flexDirection: 'column',
  },
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
}));

const StyledContactInfoContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  marginTop: '1.5rem',
  gap: '1rem',
}));

const StyledDetailsIcon = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  width: 20,
}));

const StyledContactInfoIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  [theme.breakpoints.up('xs')]: {
    width: 20,
  },
}));

const ModalComponent = ({
  date,
  options,
  comments,
  userName,
  amenityData,
  contactPersonData,
  personsEmail,
  personsPhoneNumber,
  handleClose,
  handleSelectStatus,
  handleEditReservation,
  onSaveStatus,
  chosenStatus,
  initialStatus,
  buildingName,
  createdAt,
  creatorName,
  unitName,
  buildingId,
  unitId,
  urlToCreator,
  urlToContactPerson,
}) => {
  const { t } = useTransl();
  return (
    <Box>
      <Typography
        variant="h5"
        sx={(theme) => ({
          color: theme.palette.primary.main,
          paddingBottom: 0,
        })}
      >
        <StyledLink to={`/building/${buildingId}`}>{buildingName}</StyledLink>{' '}
        {unitName && '-'}{' '}
        <StyledLink to={`/building/${buildingId}/units/${unitId}`}>
          {unitName}
        </StyledLink>
      </Typography>
      <Box>
        {createdAt} {t('metaInfo.by')}{' '}
        {urlToCreator ? (
          <StyledLink to={urlToCreator}>{creatorName}</StyledLink>
        ) : (
          creatorName
        )}
      </Box>

      <StyledPhotoContainer>
        <ExpandableImageComponent
          src={contactPersonData?.photo?.url || avatarPlaceholderSRC}
          alt={t('altLabel.userPhoto')}
          height="10rem"
          isSquare
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={(theme) => ({
              color: theme.palette.primary.main,
              display: 'flex',
              flexDirection: 'row',
            })}
          >
            {urlToContactPerson ? (
              <StyledLink to={urlToContactPerson}>{userName}</StyledLink>
            ) : (
              userName
            )}
          </Typography>
          <Box>
            {personsPhoneNumber && (
              <StyledContactInfoContainer>
                <StyledContactInfoIcon>
                  <img src={PhoneIcon} alt={t('altLabel.phone')} />
                </StyledContactInfoIcon>
                <StyledParagraph>{personsPhoneNumber}</StyledParagraph>
              </StyledContactInfoContainer>
            )}
            {personsEmail && (
              <StyledContactInfoContainer>
                <StyledContactInfoIcon>
                  <img src={LetterIcon} alt={t('altLabel.letter')} />
                </StyledContactInfoIcon>
                <StyledParagraph>{personsEmail}</StyledParagraph>
              </StyledContactInfoContainer>
            )}
          </Box>
        </Box>
      </StyledPhotoContainer>

      <StyledContactInfoContainer>
        <StyledDetailsIcon>
          <LocationIcon />
        </StyledDetailsIcon>
        <StyledParagraph>{amenityData?.name}</StyledParagraph>
      </StyledContactInfoContainer>

      <StyledContactInfoContainer>
        <StyledDetailsIcon>
          <ClockIcon />
        </StyledDetailsIcon>
        <StyledParagraph>{date}</StyledParagraph>
      </StyledContactInfoContainer>
      <StyledContactInfoContainer>
        <Box display="flex" justifyContent="center">
          <StyledParagraph fontWeight="bold">
            {t('title.comment')}: &nbsp;
          </StyledParagraph>
        </Box>
        <StyledParagraph>
          <WithLink>{comments}</WithLink>
        </StyledParagraph>
      </StyledContactInfoContainer>
      <Box mt="3rem">
        <FormControl fullWidth>
          <FormLabel>
            <StyledParagraph fontWeight="bold">
              {t('title.status')}
            </StyledParagraph>
          </FormLabel>
          <Select
            disabled={isAdmin()}
            size="small"
            defaultValue={initialStatus}
            renderValue={(value) =>
              value.charAt(0).toUpperCase() + value.slice(1)
            }
            onChange={(event) =>
              handleSelectStatus({ status: event.target.value })
            }
          >
            {options
              .filter((option) => option.value !== 'pending')
              .map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: '1.5rem',
          gap: '1rem',
        }}
      >
        <Box display="flex" flexDirection="row" gap="1rem">
          {Boolean(chosenStatus) && (
            <Button
              onClick={onSaveStatus}
              type="submit"
              variant="contained"
              color="success"
              sx={(theme) => ({ color: theme.palette.common.white })}
              disableElevation
            >
              {t('actions.save')}
            </Button>
          )}
          <Button
            onClick={handleClose}
            type="submit"
            variant="contained"
            sx={(theme) => ({ backgroundColor: theme.palette.grey[500] })}
            disableElevation
          >
            {t('actions.cancel')}
          </Button>
        </Box>
        <Button
          onClick={handleEditReservation}
          type="submit"
          variant="contained"
          color="primary"
          disableElevation
        >
          <Box display="flex" justifyContent="center" alignItems="center">
            <img
              src={PenIcon}
              alt={t('altLabel.community')}
              style={{ marginRight: '1rem' }}
            />
            {t('amenity.editAmenityReservation')}
          </Box>
        </Button>
      </Box>
    </Box>
  );
};

ModalComponent.propTypes = {
  date: PropTypes.string,
  options: PropTypes.instanceOf(Array),
  comments: PropTypes.string,
  userName: PropTypes.string,
  personsEmail: PropTypes.string,
  personsPhoneNumber: PropTypes.string,
  amenityData: PropTypes.instanceOf(Object).isRequired,
  contactPersonData: PropTypes.instanceOf(Object),
  handleClose: PropTypes.func.isRequired,
  handleSelectStatus: PropTypes.func.isRequired,
  handleEditReservation: PropTypes.func.isRequired,
  onSaveStatus: PropTypes.func.isRequired,
  chosenStatus: PropTypes.string,
  initialStatus: PropTypes.string,
  buildingName: PropTypes.string,
  createdAt: PropTypes.string,
  creatorName: PropTypes.string,
  unitName: PropTypes.string,
  buildingId: PropTypes.number,
  unitId: PropTypes.number,
  urlToCreator: PropTypes.string,
  urlToContactPerson: PropTypes.string,
};

export default ModalComponent;
